import { checkIfTextExists, sortByProperty, updateState } from "config/utils"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { GetEmployeeInterface, CreateEmployeeInterface, GetAllEmployeesInterface } from "@type/employees.types"
import { employeesService } from "services"
import { AppThunk, RootState } from "store"
import { lowerCase } from "lodash"

export interface EmployeeStateInterface {
	list: GetAllEmployeesInterface[]
	inactiveEmployeeList: GetEmployeeInterface[]
	activeEmployeeList: GetEmployeeInterface[]
	loading: boolean
	saving: boolean
	isAllEmployeeLoaded: boolean
}

const initialState: EmployeeStateInterface = {
	list: [],
	inactiveEmployeeList: [],
	activeEmployeeList: [],
	loading: false,
	saving: false,
	isAllEmployeeLoaded: false,
}

const detailSlice = createSlice({
	name: "detail",
	initialState,
	reducers: {
		fetchingEmployees: employee => {
			employee.loading = true
		},
		inActiveEmployeesFetched: (state, action: PayloadAction<GetEmployeeInterface[]>) => {
			state.loading = false
			// state.inactiveEmployeeList = [...action.payload]
			const [updatedState] = updateState(state.inactiveEmployeeList, action.payload, "_id")
			state.inactiveEmployeeList = updatedState
		},
		activeEmployeesFetched: (state, action: PayloadAction<GetEmployeeInterface[]>) => {
			state.loading = false
			// state.activeEmployeeList = [...action.payload]
			const [updatedState] = updateState(state.activeEmployeeList, action.payload, "_id")
			state.activeEmployeeList = updatedState
		},
		employeesFetched: (employee, action: PayloadAction<GetAllEmployeesInterface[]>) => {
			employee.loading = false
			employee.list = action.payload
		},
		employeesFetchingFailed: employee => {
			employee.loading = false
		},
		savingEmployee: employeeData => {
			employeeData.saving = true
		},
		employeeSaved: (state, action: PayloadAction<GetEmployeeInterface>) => {
			state.saving = false
			const { activeEmployeeList, inactiveEmployeeList } = state
			const isActiveEmployee = action.payload.isActive

			const list = isActiveEmployee ? activeEmployeeList : inactiveEmployeeList

			const [updatedState] = updateState(list, action.payload, "_id")

			updatedState.sort((a, b) =>
				String(`${a.firstName} ${a.lastName}`).localeCompare(`${b.firstName} ${b.lastName}`),
			)

			if (isActiveEmployee) state.activeEmployeeList = [...updatedState]
			else state.inactiveEmployeeList = [...updatedState]
		},
		updateEmployeeByEmployeeIdAndIsActive: (
			state,
			action: PayloadAction<{ employeeId: string; isActive: boolean }>,
		) => {
			state.saving = false
			const { employeeId, isActive } = action.payload
			const { activeEmployeeList, inactiveEmployeeList } = state

			const foundEmployee = [...activeEmployeeList, ...inactiveEmployeeList]?.find(emp => emp._id === employeeId)

			if (!foundEmployee) return
			if (foundEmployee.isActive === isActive) return

			const updatedEmployee = { ...foundEmployee, isActive }

			const sourceList = foundEmployee.isActive ? activeEmployeeList : inactiveEmployeeList
			const targetList = foundEmployee.isActive ? inactiveEmployeeList : activeEmployeeList

			const newSourceList = sourceList.filter(list => list._id !== employeeId)

			const [newTargetList] = updateState(targetList, updatedEmployee, "_id")

			newTargetList.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))

			if (foundEmployee.isActive) {
				state.activeEmployeeList = newSourceList
				state.inactiveEmployeeList = newTargetList
			} else {
				state.inactiveEmployeeList = newSourceList
				state.activeEmployeeList = newTargetList
			}
		},
		employeeSavingFailed: employee => {
			employee.saving = false
		},
		ClearEmployeeList: employee => {
			employee.list = []
			employee.activeEmployeeList = []
			employee.inactiveEmployeeList = []
		},

		setAllEmployeeLoadedState: (state, action: PayloadAction<boolean>) => {
			if (action.payload) {
				state.isAllEmployeeLoaded = action.payload
			}
		},
	},
})

//REDUCER
export default detailSlice.reducer

//ACTIONS
export const {
	fetchingEmployees,
	employeesFetched,
	inActiveEmployeesFetched,
	activeEmployeesFetched,
	employeesFetchingFailed,
	savingEmployee,
	employeeSaved,
	employeeSavingFailed,
	ClearEmployeeList,
	setAllEmployeeLoadedState,
	updateEmployeeByEmployeeIdAndIsActive,
} = detailSlice.actions

const getEmployees = (): AppThunk => async dispatch => {
	try {
		dispatch(fetchingEmployees())
		const { data: employeeResponse } = await employeesService.getAllEmployees()
		dispatch(employeesFetched(employeeResponse))
	} catch (error) {
		dispatch(employeesFetchingFailed())
	}
}

const getAllActiveEmployees =
	(isActive: boolean): AppThunk =>
	async (dispatch, getState) => {
		try {
			const isAllEmployeeLoaded = getState().employee.details.isAllEmployeeLoaded
			const activeList = getState().employee.details.activeEmployeeList
			const inactiveList = getState().employee.details.inactiveEmployeeList

			if (getState().employee.details.loading) return
			if (isAllEmployeeLoaded && isActive && !!activeList.length) return
			if (isAllEmployeeLoaded && !isActive && !!inactiveList.length) return

			if (!isActive) dispatch(setAllEmployeeLoadedState(true))

			dispatch(fetchingEmployees())
			const { data: employeeResponse } = await employeesService.getEmployeeByActiveStatus(isActive)
			dispatch(isActive ? activeEmployeesFetched(employeeResponse) : inActiveEmployeesFetched(employeeResponse))
		} catch (error) {
			dispatch(employeesFetchingFailed())
		}
	}

const getEmployeeById =
	(id: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			const list = [
				...getState().employee.details.activeEmployeeList,
				...getState().employee.details.inactiveEmployeeList,
			]
			const employeeFound = list.find(item => item._id === id)

			if (employeeFound) return

			dispatch(fetchingEmployees())
			const { data: employeeResponse } = await employeesService.getEmployeeById(id)
			dispatch(
				employeeResponse.isActive
					? activeEmployeesFetched([employeeResponse])
					: inActiveEmployeesFetched([employeeResponse]),
			)
		} catch (error) {
			dispatch(employeesFetchingFailed())
		}
	}

const saveEmployee =
	(payload: CreateEmployeeInterface, cb?: (id: string) => void): AppThunk =>
	async dispatch => {
		let data = null
		try {
			const isBeingUpdated = !!payload?._id
			dispatch(savingEmployee())
			if (isBeingUpdated) data = await employeesService.updateEmployee(payload._id ?? "", payload)
			else data = await employeesService.createEmployee(payload)

			const { data: employeeResponse } = data
			dispatch(employeeSaved(employeeResponse))
			cb?.(employeeResponse._id)
			//dispatch(setEmployeeGlobally(employeeResponse._id))
		} catch (error) {
			dispatch(employeeSavingFailed())
		}
	}

const saveEmployeeBySockets =
	(payload: GetEmployeeInterface): AppThunk =>
	(dispatch, getState) => {
		if (
			getState().globalState.selectedEmployeeBranch === payload?.branch ||
			getState().globalState.selectedSupplier === payload?.supplier
		) {
			dispatch(employeeSaved({ ...payload }))
		}
	}

export { getEmployees, saveEmployee, getAllActiveEmployees, getEmployeeById, saveEmployeeBySockets }

//SELECTORS
const selectEmployeeState = (state: RootState) => state.employee.details
const isEmployeeLoading = () => (state: RootState) => selectEmployeeState(state).loading
const isEmployeeSaving = () => (state: RootState) => selectEmployeeState(state).saving
const selectEmployeeList = () => (state: RootState) => selectEmployeeState(state).list
const selectActiveEmployeeList = () => (state: RootState) => selectEmployeeState(state).activeEmployeeList
const selectInactiveEmployeeList = () => (state: RootState) => selectEmployeeState(state).inactiveEmployeeList
const selectLoadingEmployee = () => (state: RootState) => selectEmployeeState(state).loading
// const selectEmployeeById = (employeeId: string) => (state: RootState) =>
// 	selectEmployeeList()(state)?.find(employee => employee._id === employeeId)

// const selectEmployeeListByBranch = (branchId: string | null) => {
// 	return createSelector(
// 		(state: RootState) => {
// 			const { list, inactiveList, activeSelected } = state.employee.details
// 			return activeSelected ? list : inactiveList
// 		},
// 		employeeList => (branchId ? employeeList?.filter(i => i.branch === branchId) : employeeList),
// 	)
// }

const selectActiveEmployeeByEmployeeId = (employeeId: string) =>
	createSelector(
		(state: RootState) => [state.employee.details.activeEmployeeList],
		([activeEmployeeList]) => {
			return [...activeEmployeeList]?.find(employee => employee._id === employeeId)
		},
	)

// Reports
const selectEmployeeListByField = (fieldName: string, fieldValue: string) => {
	return createSelector(
		(state: RootState) => state.employee.details.list,
		employeeList =>
			lowerCase(fieldName) && fieldValue
				? employeeList?.filter(
						item => item?.[lowerCase(fieldName) as keyof GetAllEmployeesInterface] === fieldValue,
				  )
				: [],
	)
}

// TODO 1647 - Looks shady
const selectSelectedActiveOrInactiveEmployeeDetails = (selectedEmployee: string) =>
	createSelector(
		(state: RootState) => ({
			activeEmployeeList: state.employee.details.activeEmployeeList,
			inactiveEmployeeList: state.employee.details.inactiveEmployeeList,
			selectedEmployee,
		}),
		({ activeEmployeeList, inactiveEmployeeList, selectedEmployee }) => {
			return [...activeEmployeeList, ...inactiveEmployeeList]?.find(({ _id }) => _id === selectedEmployee)
		},
	)

// Reports
const selectEmployeeListSorted = () =>
	createSelector(
		(state: RootState) => state.employee.details.list,
		employeeList => {
			return employeeList
				? [...employeeList]?.sort((a, b) => a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()))
				: []
		},
	)

const filterEmployeeByBranchOrSupplierName = (filterId: string, filterFrom: string) =>
	createSelector(
		(state: RootState) => state.employee.details.activeEmployeeList,
		employeeList => {
			if (!employeeList) return []

			let filteredData = employeeList

			if (filterId && !["Select Branch", "Select Supplier"].includes(filterId)) {
				filteredData = employeeList.filter(
					employee => employee[filterFrom as keyof CreateEmployeeInterface] === filterId,
				)
			}

			return filteredData
				.map(filteredEmployee => ({
					label: `${filteredEmployee.shortName}`,
					value: filteredEmployee._id,
				}))
				.sort(sortByProperty("label"))
		},
	)

const employeeListByMultipleBranchOrSupplierName = (filterId: string[], filterFrom: "branch" | "supplier" | "") =>
	createSelector(
		(state: RootState) => state.employee?.details?.list ?? [],
		(employeeList = []) => {
			if (!employeeList) return []

			let filteredData: GetAllEmployeesInterface[] = [...employeeList]

			if (filterId.length && filterFrom) {
				filteredData = employeeList.filter(employee => filterId.includes(employee[filterFrom]))
			}

			return filteredData.sort(sortByProperty("firstName"))
		},
	)

const selectEmployeeDropDown = () =>
	createSelector(
		(state: RootState) => state.employee.details.list,
		employeeList =>
			employeeList
				? employeeList
						?.map(i => ({ _id: i._id, name: `${i.firstName} ${i.lastName}` }))
						.sort(sortByProperty("name"))
				: [],
	)

const selectAllEmployeeList = () =>
	createSelector(
		(state: RootState) => [
			...state.employee.details.activeEmployeeList,
			...state.employee.details.inactiveEmployeeList,
		],
		list => list ?? [],
	)

const selectAllEmployeeDropDown = () =>
	createSelector(
		(state: RootState) => [
			...state.employee.details.activeEmployeeList,
			...state.employee.details.inactiveEmployeeList,
		],
		employeeList =>
			employeeList
				? employeeList
						?.map(i => ({ value: i._id, label: `${i.firstName} ${i.lastName}` }))
						.sort(sortByProperty("name"))
				: [],
	)

const selectActiveEmployeeDropdown = () =>
	createSelector(
		(state: RootState) => state.employee.details.activeEmployeeList,
		employeeList =>
			employeeList
				? employeeList
						?.map(i => ({ _id: i._id, name: `${i.firstName} ${i.lastName}` }))
						.sort(sortByProperty("name"))
				: [],
	)

// User Access Modal
const selectSortedEmployeeList = () =>
	createSelector(
		(state: RootState) => state.employee.details.list,
		(employeeList = []) => [...(employeeList || [])].sort(sortByProperty("shortName")),
	)

const filteredListBySearch = (
	searchOn: string,
	searchOnFields: string[],
	isActive: boolean,
	branchId?: string,
	supplierId?: string,
) => {
	return createSelector(
		(state: RootState) => [state.employee.details.activeEmployeeList, state.employee.details.inactiveEmployeeList],
		([activeEmployeeList, inActiveEmployeeList]) => {
			const sortedEmployeeList: GetEmployeeInterface[] = checkIfTextExists(
				(isActive ? activeEmployeeList : inActiveEmployeeList)?.filter(employee => {
					if (!supplierId) return employee.branch === branchId
					return employee?.supplier === supplierId
				}),
				searchOn,
				searchOnFields,
			).sort((a: GetEmployeeInterface, b: GetEmployeeInterface) =>
				String(`${a.firstName} ${a.lastName}`).localeCompare(`${b.firstName} ${b.lastName}`),
			)

			return sortedEmployeeList ?? []
		},
	)
}

export {
	selectActiveEmployeeByEmployeeId,
	filteredListBySearch,
	selectEmployeeListSorted,
	selectEmployeeList,
	selectActiveEmployeeList,
	selectInactiveEmployeeList,
	selectEmployeeState,
	selectLoadingEmployee,
	selectAllEmployeeList,
	isEmployeeLoading,
	isEmployeeSaving,
	selectEmployeeDropDown,
	selectActiveEmployeeDropdown,
	selectAllEmployeeDropDown,
	selectEmployeeListByField,
	filterEmployeeByBranchOrSupplierName,
	employeeListByMultipleBranchOrSupplierName,
	selectSelectedActiveOrInactiveEmployeeDetails,
	selectSortedEmployeeList,
}
